/* global process */
import axios from 'axios';
import pool from '@ricokahler/pool';
import { compact, range, uniq } from 'lodash';
import createMetadataAPIActions from './api-metadata-actions';
import { API_PATH } from '../../constants';
import { replaceParameters, constructURL } from './api-metadata-utils';

const cleanIds = ids => {
  if (!ids) return [];
  ids = Array.isArray(ids) ? ids : [ids];
  return uniq(ids.flat().filter(Boolean));
};

export const fetchObjectsPage = (
  url,
  headers,
  dispatch,
  actions,
  replace,
  fullFetch,
  except,
  abortController,
  maxPages = Infinity,
  page = 1
) => {
  const params = page == 1 ? {} : { page: page };
  const axiosParams = {
    params,
    retry: 3,
    retryDelay: 1000,
    headers: headers,
    signal: abortController.signal
  };

  const promise = axios
    .get(url, axiosParams)
    .then(response => {
      if (page == 1 && response.headers['total']) {
        // we've loaded the first page, and this endpoint is paginated
        // detect the total number of pages and start loading them
        // concurrently with a maxConcurrency as defined below
        const totalPages = Math.ceil(response.headers['total'] / response.headers['per-page']);
        const pages = range(2, Math.min(totalPages + 1, maxPages + 1));

        pool({
          collection: pages,
          maxConcurrency: 3,
          task: page =>
            fetchObjectsPage(
              url,
              headers,
              dispatch,
              actions,
              replace,
              fullFetch,
              except,
              abortController,
              maxPages,
              page
            )
        });
      }

      const loadingStatus = {
        page: response.headers['page'] || null,
        perPage: response.headers['per-page'] || null,
        total: response.headers['total'] || null,
        pagesLoaded: compact([response.headers['page']])
      };

      // Only the first page should replace, other pages should not replace
      replace = page === 1 && !!replace;

      dispatch(actions.fetchSuccessful(response.data, { replace: !!replace }, loadingStatus, !!fullFetch, except));
    })
    .catch(error => {
      console.error(error);
      if (error.message === 'canceled') {
        // Cancels do not contain a response per se, only a message
        const { message } = error;
        dispatch(actions.fetchFailed({ statusText: message, status: message }));
      } else {
        const { statusText, status } = error.response;
        dispatch(actions.fetchFailed({ statusText, status }));
      }

      throw error;
    });

  promise.abortController = abortController;

  return promise;
};

export const fetchObjects = (url, headers, dispatch, actions, replace, fullFetch, except, maxPages) => {
  dispatch(actions.startFetch());

  const abortController = new AbortController();

  return fetchObjectsPage(url, headers, dispatch, actions, replace, fullFetch, except, abortController, maxPages);
};

export default ({ baseType, apiEndpoint, name, createNew, singularAssociation = false }) => {
  createNew = createNew || (() => ({}));

  if (!baseType) {
    throw new Error('Basetype is not defined');
  }
  if (!apiEndpoint) {
    throw new Error('apiEndpoint is not defined');
  }
  if (!name) {
    throw new Error(`Name is not defined ${baseType}`);
  }

  const actions = createMetadataAPIActions({ baseType });

  return {
    // positional params are painful with lots of nulls, and making sure they
    // are in the right place. This method below supports gradual migration to
    // an param object
    fetch:
      (...args) =>
      (dispatch, getState) => {
        let ids, params, headers, forceFetch, replace, onlyMissing, except, maxPages;
        // check if we have a param object (preferred). If not, we expect
        // (deprecated) positional params
        if (args.length === 1 && typeof args[0] === 'object' && !Array.isArray(args[0])) {
          ({ ids, params, headers, forceFetch, replace, onlyMissing, except, maxPages } = args[0]);
        } else {
          [ids, params, headers, forceFetch, replace, except, maxPages] = args;
        }

        if (args.length > 1) {
          console.log('DEPRECATED: use param object instead of positional when calling fetch');
          console.log(baseType, ids, params, headers, forceFetch, replace);
          if (process.env.NODE_ENV !== 'production') {
            alert('DEPRECATED: use param object instead of positional when calling fetch');
            throw new Error('DEPRECATED: use param object instead of positional when calling fetch');
          }
        }

        ids = cleanIds(ids);

        if (onlyMissing) {
          const missingIds = ids.filter(id => !getState()[name].byId[id]);
          if (missingIds.length === 0) {
            console.log(`All objects already loaded: not fetching`);
            return dispatch(actions.fetchSuccessful([], { replace: false }, {}, false));
          }

          replace = false; // we never replace when only loading missing objects
          ids = missingIds;
        }

        // if no ids were passed, the API will return all accessible objects. In
        // this case, we check first that this wasn't already loaded before to
        // save redundant queries. After a successful fetch, all objects are
        // flushed, because we retrieved a fresh version of all objects of this
        // type.
        const fullFetchComplete = !!getState()[name]?.fullFetchComplete;
        const fullFetch = ids.length === 0;

        if (fullFetch) {
          // When loading, it will not fetch again so a check was added to
          // verify if it is a forceFetch or not in order to make another fetch.
          if (!forceFetch && getState()[name].loading) {
            console.log(`Already loading ${name}: not fetching`);
            return Promise.resolve();
          }
          const lastFetchAgo = Date.now() - getState()[name].lastFetch;

          if (!forceFetch && fullFetchComplete && lastFetchAgo < 60 * 1000) {
            // don't refetch if last fetch is <1 min ago and the fetch
            // is not forced and a full fetch was done already
            console.log(`Still fresh ${name}: not fetching`);
            return Promise.resolve();
          }
        }

        const url = constructURL(apiEndpoint, ids, params);
        // Once tagged as full fetch, it will remain as full fetch
        return fetchObjects(url, headers, dispatch, actions, replace, fullFetch, except, maxPages);
      },
    storeInBackend:
      (id, options = {}) =>
      (dispatch, getState) => {
        const { headers } = options;
        const { params } = options;
        const { modifications } = options;
        const fullObject = getState()[name].byId[id];

        if (!fullObject) throw `Object with ID: ${id} does not exist in redux`;

        // don't POST back validationErrors
        const { validationErrors, ...object } = fullObject;

        const finalObject = modifications === undefined ? object : { ...object, ...modifications };

        const handleError = error => {
          const { response } = error;
          if (response.status === 422) {
            return dispatch(actions.validationErrorsReceived(finalObject.id, response.data.validation_errors));
          } else {
            throw error;
          }
        };

        if (finalObject.transient) {
          let url = `${API_PATH}/${apiEndpoint}`;
          url = replaceParameters(url, finalObject);
          return axios
            .post(url, finalObject, { headers, params })
            .then(response => dispatch(actions.createInBackendSuccessful(response.data, finalObject.id)))
            .catch(handleError);
        } else {
          let url = `${API_PATH}/${apiEndpoint}/`;
          url = singularAssociation ? url : `${url}${finalObject.id}`;
          url = replaceParameters(url, finalObject);
          return axios
            .put(url, finalObject, { params })
            .then(response => dispatch(actions.saveToBackendSuccessful(response.data)))
            .catch(handleError);
        }
      },
    createNew: initialValue => dispatch => {
      // create a new object using the default template, then override these
      // with fields set in the initialValue
      const newObject = { ...createNew(), ...initialValue };
      newObject.id = globalThis.crypto.randomUUID();
      newObject.transient = true; // object has not been stored in backend
      dispatch(actions.newObjectCreated(newObject));
      return Promise.resolve(newObject);
    },
    createNewInBulk: initialValues => dispatch => {
      const newObjects = initialValues.map(initialValue => {
        const newObject = { ...createNew(), ...initialValue };
        newObject.id = globalThis.crypto.randomUUID();
        newObject.transient = true; // object has not been stored in backend
        return newObject;
      });
      dispatch(actions.newObjectsCreated(newObjects));
      return Promise.resolve(newObjects);
    },
    updateFields: (id, values) => dispatch => {
      const action = dispatch(actions.fieldsUpdated(id, values));
      return Promise.resolve(action);
    },
    deleteFromBackend: id => dispatch => {
      const url = `${API_PATH}/${apiEndpoint}/${id}`;
      return axios.delete(url).then(() => dispatch(actions.deleteFromBackendSuccessful(id)));
    },
    purge: () => dispatch => {
      const action = dispatch(actions.purge());
      return Promise.resolve(action);
    }
  };
};
